import React, { useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';
import { FormApi } from 'final-form';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { IApiResponse } from 'utils/models';
import Form from 'ui/Form/Form';
import { requiredOnly, validatePhone, validationCallback } from 'utils/validation';
import Input from 'ui/Input/Input';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import Button, { Sizes as ButtonSizes, Themes as ButtonThemes } from 'ui/Button/Button';
import { StatusModal, WarningModal } from 'components/modals';
import NavigateBackStep from 'ui/navigateBackStep/NavigateBackStep';
import api from 'api/api';
import { onlyNCharacters, onlyNumbers } from 'utils/formatters';
import InputHint from 'components/modules/Beneficiaries/InputHint/InputHint';
import { IApiPrepaidCardBen } from 'components/modules/Beneficiaries/Beneficiaries.model';
import debounce from 'utils/debounce';
import { IBeneficiaryPrepaidCard } from './SinglePrepaidBen.model';

import deleteIcon from 'assets/icons/delete.png';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

const schema = yup.object().shape({
  name: requiredOnly('Full name'),
  cardCustomerId: requiredOnly('Customer ID'),
  cardName: requiredOnly('Card name'),
  phoneNumber: validatePhone(),
  last4digit: requiredOnly('Card’s last 4 digits').min(4, '4 digits required'),
});

interface IFormValues {
  name: string;
  cardCustomerId: string;
  cardName: string;
  phoneNumber: string;
  last4digit: string;
}

interface IEditBenFab {
  data: IBeneficiaryPrepaidCard;
  onClose: () => void;
}

const EditPrepaidCardBen = ({ data, onClose }: IEditBenFab) => {
  const [nameHint, setNameHint] = useState('');
  const [isLookingUp, setLookingUp] = useState(false);
  const clearNameHint = () => setNameHint('');
  const [validatedAccountNo, setValidatedAccountNo] = useState(true);

  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const [actionType, setActionType] = useState<string>('');
  const [showWarning, setShowWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const openWarning = (type: string) => () => {
    setActionType(type);
    setShowWarning(true);
  };
  const closeWarning = () => {
    setShowWarning(false);
  };

  const [showStatus, setShowStatus] = useState(false);
  const onCloseStatusModal = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowStatus(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
    onClose();
  };

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const validate = useCallback((values: IFormValues) => validationCallback<IFormValues>(schema)(values), []);

  const intialValues: IFormValues = useMemo(() => {
    const iniitalData = {
      name: data.name,
      cardCustomerId: data.cardCustomerId,
      cardName: data.cardName,
      phoneNumber: data.phoneNumber,
      last4digit: data.last4digit,
    };

    return iniitalData;
  }, []);

  const lookupAccount = (form: FormType, customerId: string) => {
    api
      .get(`/transfer/validatePrepaidCard?customerId=${String(customerId)}`, {}, true, true)
      .then((response: IApiPrepaidCardBen) => {
        setLookingUp(false);
        setValidatedAccountNo(true);
        setNameHint('');
        form.mutators.changeFieldValue('cardName', response.customerResponse.preferredNameField);
        form.mutators.changeFieldValue('phoneNumber', response.customerResponse.mobilePhoneNumberField);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLookingUp(false);
        setValidatedAccountNo(true);
        setNameHint(error.description);
        form.mutators.changeFieldValue('cardName', '');
        form.mutators.changeFieldValue('phoneNumber', '');
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 500), []);

  const handleChangeCustomerId = (form:FormType) => ({ target }) => {
    setLookingUp(true);
    setValidatedAccountNo(false);
    debounced(form, target.value as string);
  };

  const onDeleteBeneficiary = (id: number) => {
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/beneficiary/prepaidcard/delete', {
        beneficiaryId: id,
      })
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const onSaveBeneficiary = (id: number, values: IFormValues) => {
    const body = {
      ...values,
      beneficiaryId: id,
    };

    api
      .put('/beneficiary/prepaidcard/update', body)
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const openConfirm = (values: IFormValues) => () => {
    setSubmitStatus(AxiosStatus.PENDING);

    return actionType === 'delete' ? onDeleteBeneficiary(data.id) : onSaveBeneficiary(data.id, values);
  };

  return (
    <>
      <Form
        onSubmit={openWarning('save')}
        validate={validate}
        mutators={{ changeFieldValue, setFieldTouched }}
        initialValues={intialValues}
        render={({ handleSubmit, values, form, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            {!downMdScreen && <NavigateBackStep className="mt30" onClick={onClose}>Back</NavigateBackStep>}
            <FieldsLeveler
              label={(
                <>
                  <div>Beneficiary details</div>
                  {!downMdScreen && (
                    <Button size={ButtonSizes.small} onClick={openWarning('delete')}>
                      <img src={deleteIcon} alt="trash-icon" />
                      &nbsp;Delete
                    </Button>
                  )}
                </>
              )}
            >
              <Input name="name" label="Full Name" disabled={submitting} />
              <Input
                name="cardCustomerId"
                label="Customer ID"
                disabled={submitting}
                onInput={handleChangeCustomerId(form)}
                fieldProps={{
                  parse: (value: string) => onlyNCharacters(10)(onlyNumbers(value)),
                }}
              />
              {(nameHint || isLookingUp) && <InputHint hint={nameHint} loading={isLookingUp} onClose={clearNameHint} />}
              <Input name="cardName" label="Card Name" disabled />
              <Input name="phoneNumber" label="Phone Number" disabled />
              <Input
                name="last4digit"
                label="Card’s last 4 digits"
                disabled={submitting}
                fieldProps={{
                  parse: (value: string) => onlyNCharacters(4)(onlyNumbers(value)),
                }}
              />
            </FieldsLeveler>
            <SubmitLeveler>
              {downMdScreen && (
                <Button
                  type="button"
                  theme={ButtonThemes.secondary}
                  fullWidth
                  className="mb20"
                  onClick={openWarning('delete')}
                >
                  Delete Beneficiary
                </Button>
              )}
              <Button
                type="submit"
                fullWidth
                disabled={
                  submitting || !form.getState().dirty || !validatedAccountNo || isLookingUp || hasValidationErrors
                  }
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </SubmitLeveler>
            <WarningModal
              open={showWarning}
              submitting={submitting}
              submitName={actionType === 'delete' ? 'Delete' : 'Save'}
              onSubmit={openConfirm(values)}
              onClose={closeWarning}
            >
              {actionType === 'delete'
                ? 'Are you sure you want to delete the Beneficiary?'
                : 'Save changes to Beneficiary?'}
            </WarningModal>
            <StatusModal
              open={showStatus}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onRetry={onCloseStatusModal}
              onClose={onCloseStatusModal}
              onRetryText="Continue"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMessage : errorMessage}
            </StatusModal>
          </form>
        )}
      />
    </>
  );
};

export default EditPrepaidCardBen;
